import { CSSInterpolation } from '@emotion/css/create-instance'
import React from 'react'

import { StepLabel } from './StepLabel'
import { StepLine } from './StepLine'
import type { StepProps, TimelineOrientation } from './types'
import {
  getCenterAlignGridTemplateArea,
  getLabelGridAreaName,
  getLeftAlignGridTemplateArea,
  getLineGridAreaName,
  getVerticalAlignGridTemplateArea,
  SMALL_DEVICE_BREAKPOINT,
  UNUSED_GRID_AREA_NAME,
} from './utils'

type Props = {
  /**
   * When set to `true`, will respond to screen mobile widths and adjust orientation accordingly.
   */
  isResponsive?: boolean
  orientation: TimelineOrientation
  /**
   * Pass a CSS object if you need to override styles on the root element. Please do this sparingly.
   */
  overrideRootStyles?: CSSInterpolation
  steps: StepProps[]
}

export function DO_NOT_USE_TimelineStepPropsJustForPropsDocs(
  props: StepProps // eslint-disable-line @typescript-eslint/no-unused-vars
) {
  return null
}

export function Timeline({
  isResponsive = false,
  orientation,
  overrideRootStyles,
  steps,
}: Props) {
  if (!steps.length) {
    return null
  }

  return (
    <div
      css={[
        {
          display: 'grid',
        },
        getRootStyles(orientation, steps.length),
        isResponsive && {
          [`@media screen and (max-width: ${SMALL_DEVICE_BREAKPOINT}px)`]:
            getRootStyles('vertical', steps.length),
        },
        overrideRootStyles,
      ]}
    >
      {orientation === 'horizontal-center-align' && (
        <div
          css={[
            { gridArea: UNUSED_GRID_AREA_NAME },
            isResponsive && {
              [`@media screen and (max-width: ${SMALL_DEVICE_BREAKPOINT}px)`]: {
                display: 'none',
              },
            },
          ]}
        />
      )}
      {steps.map((stepProps, index) => (
        <StepLine
          key={stepProps.label}
          gridArea={getLineGridAreaName(index)}
          isIconOnly={index === steps.length - 1}
          isResponsive={isResponsive}
          orientation={orientation}
          {...stepProps}
        />
      ))}
      {steps.map((stepProps, index) => (
        <StepLabel
          key={stepProps.label}
          gridArea={getLabelGridAreaName(index)}
          isResponsive={isResponsive}
          orientation={orientation}
          {...stepProps}
        />
      ))}
    </div>
  )
}

function getRootStyles(
  orientation: TimelineOrientation,
  numSteps: number
):
  | {
      gridColumnGap: number
      gridRowGap: number
      gridTemplateAreas: string
      gridTemplateColumns: string
      gridTemplateRows: string
    }
  | undefined {
  switch (orientation) {
    case 'horizontal-left-align': {
      return {
        gridColumnGap: 0,
        gridRowGap: 8,
        gridTemplateAreas: getLeftAlignGridTemplateArea(numSteps),
        gridTemplateColumns: `repeat(${numSteps}, minmax(170px, 1fr))`,
        gridTemplateRows: '24px 1fr',
      }
    }
    case 'horizontal-center-align': {
      return {
        gridColumnGap: 0,
        gridRowGap: 8,
        gridTemplateAreas: getCenterAlignGridTemplateArea(numSteps),
        gridTemplateColumns: `repeat(${numSteps * 2}, minmax(85px, 1fr))`,
        gridTemplateRows: '24px 1fr',
      }
    }
    case 'vertical': {
      return {
        gridColumnGap: 16,
        gridRowGap: 0,
        gridTemplateAreas: getVerticalAlignGridTemplateArea(numSteps),
        gridTemplateColumns: '24px 1fr',
        gridTemplateRows: `repeat(${numSteps}, minmax(62px, 1fr))`,
      }
    }
    default: {
      return undefined
    }
  }
}
